/**
 * The pick route: one explicit, hand-maintained ordering of everything in the freezer.
 *
 * Why this exists: packing slips used to be sorted on each product's own `purchased` counter.
 * That counter lives per shop, so a VCE slip and an HVO slip listed the same freezer in two
 * different orders - while there is only one freezer, laid out along VCE's popularity. Sorting
 * on a shared list instead makes both slips follow the same walking route, and gives the
 * order picking system (see planning/orderpicking-eigen-systeem.md) the route it needs.
 *
 * A route entry is a spot in the freezer. VCE and HVO sell the exact same physical product, so
 * one entry holds the product document from both shops and they share a single position.
 *
 * Everything in this file is pure: no Firestore, no React. The hook (data/hooks/usePickRoute)
 * does the reading and writing, this decides what the result should look like.
 */

/** Gap between positions, so an entry can be dropped between two others without renumbering. */
export const SEQUENCE_STEP = 10;

export const SHOP_VCE = "VCE";
export const SHOP_HVO = "HVO";

/**
 * Name reduced to what two shops can agree on: no case, no accents, no punctuation.
 * Used to recognise "the same product" across the two Firestore projects, which share
 * no ids. Deliberately dumb - anything it misses is linked by hand in the CMS instead of
 * being guessed at here.
 */
export const normalizeProductName = (name) =>
  (name || "")
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const productKey = (shopSource, productId) => `${shopSource}|${productId}`;

/**
 * Lookup structure for sorting. Built once per print run, then used per order line.
 *
 * Two ways in, in order of trust:
 *  - by product id + shop, which is exact
 *  - by normalized name, which catches lines whose product was replaced in the webshop
 *    but still sits on the route under the same name
 */
export const buildPickRouteIndex = (entries) => {
  const byProduct = new Map();
  const byName = new Map();

  (entries || [])
    .filter((entry) => entry && entry.active !== false)
    .forEach((entry) => {
      const sequence = entry.sequence;
      if (typeof sequence !== "number") return;

      (entry.products || []).forEach((product) => {
        if (!product || !product.productId) return;
        byProduct.set(productKey(product.shopSource, product.productId), sequence);

        const normalized = normalizeProductName(product.name);
        // First entry wins: a duplicate name must not silently move an existing product
        if (!!normalized && !byName.has(normalized)) byName.set(normalized, sequence);
      });

      const normalizedLabel = normalizeProductName(entry.label);
      if (!!normalizedLabel && !byName.has(normalizedLabel)) byName.set(normalizedLabel, sequence);
    });

  return { byProduct, byName, size: byProduct.size };
};

/**
 * Position of a single packing slip line, or null when it is not on the route yet.
 */
export const getPickSequence = (index, line) => {
  if (!index || !line) return null;

  if (!!line.productId) {
    const bySource = index.byProduct.get(productKey(line.shopSource, line.productId));
    if (bySource !== undefined) return bySource;
  }

  const normalized = normalizeProductName(line.name);
  const byName = index.byName.get(normalized);
  return byName === undefined ? null : byName;
};

/**
 * Order the lines of one packing slip along the walking route.
 *
 * Lines that are not on the route keep the picker walking: they are put after everything
 * that is, sorted by name, and flagged so the slip can mark them. Never dropped.
 * Returns new line objects with `pickSequence` and `offRoute` attached.
 */
export const sortLinesByPickRoute = (lines, index) => {
  const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "nl");

  const positioned = (lines || []).map((line) => {
    const pickSequence = getPickSequence(index, line);
    return { ...line, pickSequence, offRoute: pickSequence === null };
  });

  const known = positioned.filter((line) => !line.offRoute);
  const unknown = positioned.filter((line) => line.offRoute);

  known.sort((a, b) => (a.pickSequence !== b.pickSequence ? a.pickSequence - b.pickSequence : byName(a, b)));
  unknown.sort(byName);

  return [...known, ...unknown];
};

/**
 * Sorting used before there was a route, kept as the fallback while the list is still empty.
 * Nothing is off-route here: with no route configured there is nothing to be off, and the
 * packing slip is a customer-facing document that must not sprout markers on every line.
 */
export const sortLinesByPurchased = (lines) =>
  [...(lines || [])]
    .sort((a, b) => (b.purchased || 0) - (a.purchased || 0))
    .map((line) => ({ ...line, pickSequence: null, offRoute: false }));

/**
 * Sort one packing slip's lines: along the route when there is one, on `purchased` when
 * there is not. An empty route must never reorder anything, so the switch is explicit.
 */
export const sortPackingSlipLines = (lines, index) => {
  if (!index || index.size === 0) return sortLinesByPurchased(lines);
  return sortLinesByPickRoute(lines, index);
};

const shopProductsOf = (entry) => (entry && entry.products) || [];

/** Every `${shop}|${productId}` already placed somewhere on the route. */
const placedProductKeys = (entries) => {
  const placed = new Set();
  (entries || []).forEach((entry) =>
    shopProductsOf(entry).forEach((product) => {
      if (!!product && !!product.productId) placed.add(productKey(product.shopSource, product.productId));
    }),
  );
  return placed;
};

const toEntryProduct = (product) => ({
  shopSource: product.shopSource,
  productId: product.id,
  name: product.name || "",
});

/** Popularity of an entry, taken from VCE - the shop the freezer is actually laid out along. */
const vcePurchasedOf = (entry, purchasedByKey) => {
  const vce = shopProductsOf(entry).find((product) => product.shopSource === SHOP_VCE);
  if (!vce) return -1;
  return purchasedByKey.get(productKey(SHOP_VCE, vce.productId)) || 0;
};

/**
 * Build the route from scratch: VCE products ordered by how often they sell, with each HVO
 * product folded into the VCE entry of the same name. HVO-only products land at the end,
 * where they are easy to find and place by hand.
 *
 * Only used for the very first fill and for an explicit "reset order" - a rebuild throws
 * away manual positioning, which is the whole value of the list.
 */
export const buildRouteFromProducts = (products) => {
  const vceProducts = (products || []).filter((product) => product.shopSource === SHOP_VCE);
  const hvoProducts = (products || []).filter((product) => product.shopSource === SHOP_HVO);

  const entries = [...vceProducts]
    .sort((a, b) => (b.purchased || 0) - (a.purchased || 0))
    .map((product) => ({
      label: product.name || "",
      products: [toEntryProduct(product)],
      active: true,
    }));

  const entryByName = new Map();
  entries.forEach((entry) => {
    const normalized = normalizeProductName(entry.label);
    if (!!normalized && !entryByName.has(normalized)) entryByName.set(normalized, entry);
  });

  hvoProducts.forEach((product) => {
    const match = entryByName.get(normalizeProductName(product.name));
    if (match) {
      match.products.push(toEntryProduct(product));
      return;
    }
    entries.push({ label: product.name || "", products: [toEntryProduct(product)], active: true });
  });

  return withSequences(entries);
};

/** (Re)number a list so its array order becomes its route order. */
export const withSequences = (entries) =>
  (entries || []).map((entry, position) => ({ ...entry, sequence: (position + 1) * SEQUENCE_STEP }));

/**
 * Work out what a "update from products" run should change, without touching what is
 * already positioned. New products are attached to the entry of the same name when there is
 * one, and otherwise appended - so running this never costs someone their manual ordering.
 *
 * Returns the changes rather than applying them, so the page can show what is about to happen.
 */
export const buildRouteSync = (entries, products) => {
  const existing = entries || [];
  const placed = placedProductKeys(existing);

  const entryByName = new Map();
  existing.forEach((entry) => {
    const normalized = normalizeProductName(entry.label);
    if (!!normalized && !entryByName.has(normalized)) entryByName.set(normalized, entry);
    shopProductsOf(entry).forEach((product) => {
      const productNormalized = normalizeProductName(product.name);
      if (!!productNormalized && !entryByName.has(productNormalized)) entryByName.set(productNormalized, entry);
    });
  });

  const missing = (products || []).filter(
    (product) => !!product.id && !placed.has(productKey(product.shopSource, product.id)),
  );

  // Attaching to an existing entry, keyed by entry id so two new products can join the same spot
  const additionsByEntry = new Map();
  const created = [];

  let nextSequence =
    existing.reduce((highest, entry) => Math.max(highest, entry.sequence || 0), 0) + SEQUENCE_STEP;

  missing.forEach((product) => {
    const match = entryByName.get(normalizeProductName(product.name));
    if (match) {
      if (!!match.id) {
        const additions = additionsByEntry.get(match.id) || { entry: match, products: [] };
        additions.products.push(toEntryProduct(product));
        additionsByEntry.set(match.id, additions);
      } else {
        // An entry created earlier in this same run, so it is not in Firestore yet and has no
        // id to key an update on - the product just joins it before it gets written
        match.products.push(toEntryProduct(product));
      }
      return;
    }
    const entry = {
      label: product.name || "",
      products: [toEntryProduct(product)],
      active: true,
      sequence: nextSequence,
    };
    nextSequence += SEQUENCE_STEP;
    created.push(entry);
    // A VCE and an HVO product with the same new name should share the entry that was just made
    const normalized = normalizeProductName(entry.label);
    if (!!normalized) entryByName.set(normalized, entry);
  });

  return {
    created,
    attached: Array.from(additionsByEntry.values()),
    // Entries pointing at products that no longer exist. Reported, never deleted automatically:
    // a product can disappear from the webshop while its box is still in the freezer.
    orphaned: findOrphanedEntries(existing, products),
  };
};

/** Entries whose shop products have all disappeared from the products collections. */
export const findOrphanedEntries = (entries, products) => {
  const known = new Set(
    (products || []).filter((product) => !!product.id).map((product) => productKey(product.shopSource, product.id)),
  );
  return (entries || []).filter((entry) => {
    const entryProducts = shopProductsOf(entry);
    if (entryProducts.length === 0) return true;
    return entryProducts.every((product) => !known.has(productKey(product.shopSource, product.productId)));
  });
};

/**
 * Renumber the existing entries by VCE popularity, keeping the entries themselves intact.
 * The destructive one: it undoes manual ordering, so the page asks first.
 */
export const buildRouteReset = (entries, products) => {
  const purchasedByKey = new Map(
    (products || [])
      .filter((product) => !!product.id)
      .map((product) => [productKey(product.shopSource, product.id), product.purchased || 0]),
  );

  const sorted = [...(entries || [])].sort((a, b) => {
    const difference = vcePurchasedOf(b, purchasedByKey) - vcePurchasedOf(a, purchasedByKey);
    if (difference !== 0) return difference;
    return (a.label || "").localeCompare(b.label || "", "nl");
  });

  return withSequences(sorted);
};

/**
 * Move one entry to a new position in the list and renumber. Used by both drag-and-drop and
 * the position input, so they can never disagree about what a position means.
 */
export const moveEntry = (entries, fromIndex, toIndex) => {
  const list = [...(entries || [])];
  if (fromIndex < 0 || fromIndex >= list.length) return list;
  const target = Math.max(0, Math.min(toIndex, list.length - 1));
  const [moved] = list.splice(fromIndex, 1);
  list.splice(target, 0, moved);
  return withSequences(list);
};

/** Shop products that are not on the route yet - what the page warns about. */
export const findUnplacedProducts = (entries, products) => {
  const placed = placedProductKeys(entries);
  return (products || []).filter(
    (product) => !!product.id && !placed.has(productKey(product.shopSource, product.id)),
  );
};
